// Common
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { UnsavedChangesGuard } from '@modules/common/services/unsaved-changes.guard';
import { accountKindReadyGuard } from '@modules/common/services/account-kind-ready.guard';
import { teamSpaceBootGuard } from '@modules/common/services/team-space-boot.guard';
import { notesHomeGuard } from '@modules/notes/routing/notes-home.guard';
import { notesWorkspaceGuard } from '@modules/notes/routing/notes-workspace.guard';
import { notesWorkspaceCanDeactivate } from '@modules/notes/routing/notes-navigation-confirmation.service';
import { sheetsEditorCanDeactivate } from '@modules/sheets/services/sheets-editor-navigation.service';

// Components
import { AdminLayoutComponent } from './components/admin-layout/admin-layout.component';
import { ContactsLayoutComponent } from './components/contacts-layout/contacts-layout.component';
import { EventsLayoutComponent } from './components/events-layout/events-layout.component';
import { FilesLayoutComponent } from './components/files-layout/files-layout.component';
import { LayoutComponent } from './components/layout/layout.component';
import { MailLayoutComponent } from './components/mail-layout/mail-layout.component';
import { SheetsLayoutComponent } from './components/sheets-layout/sheets-layout.component';
import { NotesLayoutComponent } from './components/notes-layout/notes-layout.component';
import { AtlasLayoutComponent } from './components/atlas-layout/atlas-layout.component';
import { SettingsLayoutComponent } from './components/settings-layout/settings-layout.component';
import { BoardsLayoutComponent } from '@modules/boards/components/layout/boards-layout.component';
import { boardsAppEnabledMatch, boardsCanDeactivate } from '@modules/boards/routing/boards-route-guards';
import { LazySlidesViewComponent } from '@modules/slides/components/lazy-slides-view/lazy-slides-view.component';
import { TaskingLayoutComponent } from './components/tasking-layout/tasking-layout.component';

// Shared Components
import { AdminGuard } from '@frontendlib/common/guards/admin.guard';
import { AtlasAppGuard } from '@frontendlib/common/guards/atlas-app.guard';
import { authGuard } from '@frontendlib/common/guards/auth.guard';
import { CalendarAppGuard } from '@frontendlib/common/guards/calendar-app.guard';
import { checkoutRequiredGuard } from '@frontendlib/common/guards/payment-required.guard';
import { ContactsAppGuard } from '@frontendlib/common/guards/contacts-app.guard';
import { FilesAppGuard } from '@frontendlib/common/guards/files-app.guard';
import { MailAppGuard } from '@frontendlib/common/guards/mail-app.guard';
import { NotesAppGuard } from '@frontendlib/common/guards/notes-app.guard';
import { SheetsAppGuard } from '@frontendlib/common/guards/sheets-app.guard';
import { SlidesAppGuard } from '@frontendlib/common/guards/slides-app.guard';
import { TaskingAppGuard } from '@frontendlib/common/guards/tasking-app.guard';
// The BUILD-TIME environment (the file each build configuration swaps in), as main.ts reads it.
// Not `@environment`: that one is filled in by `setEnvironment()` while the app starts, after
// this module's routes array has already been evaluated, so reading it here threw
// "Cannot read properties of undefined (reading 'production')" and the app never started.
import { environment } from '../../../environments/environment';
import { CalendarSharedComponent } from '@modules/calendar-app/components/calendar-shared/calendar-shared.component';
import { EventSharedComponent } from '@modules/calendar-app/components/event-shared/event-shared.component';
import { ContactSharedComponent } from '@modules/contacts/components/contact-shared/contact-shared.component';
import { GroupSharedComponent } from '@modules/contacts/components/group-shared/group-shared.component';
import { FileSharedComponent } from '@modules/files/components/file-shared/file-shared.component';
import { FolderSharedComponent } from '@modules/files/components/folder-shared/folder-shared.component';
import { MessageFolderSharedComponent } from '@modules/messages/components/message-folder-shared/message-folder-shared.component';
import { MessageSharedComponent } from '@modules/messages/components/message-shared/message-shared.component';
import { NoteSharedComponent } from '@modules/notes/components/note-shared/note-shared.component';
import { NotebookSharedComponent } from '@modules/notes/components/notebook-shared/notebook-shared.component';
import { SettingsWindowComponent } from '@modules/settings-window/settings-window.component';
import { settingsWindowMatcher } from '@modules/settings-window/settings-window.matcher';
import { SettingsDiscardGate } from '@modules/settings-window/account/settings-subpage.guard';
import { SettingsConfirmService } from '@modules/settings-window/kit/settings-confirm.service';
import { SettingsFocusService } from '@modules/settings-window/settings-focus.service';
import { ProjectSharedComponent } from '@modules/tasks/components/project-shared/project-shared.component';
import { TaskSharedComponent } from '@modules/tasks/components/task-shared/task-shared.component';
import { SPACES_PATH } from '@modules/common/utils/space-route';
import { chatHomeGuard } from '@modules/common/services/chat-home.guard';
import { SheetFolderSharedComponent } from '@modules/sheets/components/sheet-folder-shared/sheet-folder-shared.component';
import { SheetSharedComponent } from '@modules/sheets/components/sheet-shared/sheet-shared.component';
import { SharedLayoutComponent } from './components/shared-layout/shared-layout.component';
import { settingsWindowAudienceGuard } from './settings-window-audience.guard';
import { settingsWindowDeepLinkGuard } from './settings-window-deep-link.guard';
import { InvitationAcceptanceComponent } from '@modules/invitation-acceptance/invitation-acceptance.component';
import { JoinInvitationHostComponent } from '@modules/invitation-acceptance/join-invitation-host.component';

export const pagesRoutes: Routes = [
  {
    path: 'settings',
    canActivate: [authGuard, settingsWindowDeepLinkGuard, accountKindReadyGuard, teamSpaceBootGuard],
    component: SettingsLayoutComponent,
    loadChildren: () => import('@modules/settings/settings.module').then((m) => m.SettingsModule),
  },
  {
    path: 'admin',
    canActivate: [authGuard, AdminGuard, accountKindReadyGuard, teamSpaceBootGuard],
    component: AdminLayoutComponent,
    loadChildren: () => import('@modules/admin/admin.module').then((m) => m.AdminModule),
  },
  // The bare editor, OUTSIDE authGuard, so Sheets can be opened without a session
  // on a dev server that has no API proxy.
  //
  // NOT in a production build (B-61). Being unguarded is the whole point of it in
  // development and is exactly why it must not ship: in production it would be an
  // unauthenticated route rendering the editor and serving the ~1.9 MB the engine chunk
  // to anyone who guessed the path, in an app where every other app route sits
  // behind authGuard plus a per-app guard. It exposes no user data -- the fixture is
  // local and the grid is unbound, so nothing is saved -- but it is development
  // scaffolding and strangers have no business reaching it.
  //
  // It deliberately does NOT render SheetsLayoutComponent: that layout uses
  // app-nav-bar, which injects _MessagesService, and that provider only exists
  // inside the guarded shell -- rendering it unguarded throws NG0201 and the router
  // falls back to '/'. The preview and expand behaviour therefore live on the
  // guarded /sheets route only, and need a session to see.
  ...(environment.production
    ? []
    : [
        {
          path: 'sheets-spike',
          loadComponent: () =>
            import('@modules/sheets/sheets-spike-layout.component').then((m) => m.SheetsSpikeLayoutComponent),
        },
      ]),
  { path: '', redirectTo: '/mail', pathMatch: 'full' },
  {
    path: 'join/:token',
    component: InvitationAcceptanceComponent,
  },
  {
    path: '',
    // `teamSpaceBootGuard` runs LAST and only ever returns true: it does not gate entry, it seeds
    // the space context before `LayoutComponent` — and therefore `SpaceContextStore`, which is
    // provided by `*spaceContext="'main'"` inside its template — is constructed. A team account can
    // then never be built into a PERSONAL context and corrected afterwards (#1303, ADR-0005).
    canActivate: [authGuard, checkoutRequiredGuard, accountKindReadyGuard, teamSpaceBootGuard],
    component: LayoutComponent,
    children: [
      {
        path: 'join/:token/accept',
        component: JoinInvitationHostComponent,
      },
      {
        matcher: settingsWindowMatcher,
        outlet: 'settings',
        component: SettingsWindowComponent,
        providers: [SettingsConfirmService, SettingsDiscardGate, SettingsFocusService],
        canActivateChild: [settingsWindowAudienceGuard],
        loadChildren: () =>
          import('@modules/settings-window/settings-window.routes').then((m) => m.SETTINGS_WINDOW_ROUTES),
      },
      {
        path: 'mail',
        component: MailLayoutComponent,
        canActivate: [MailAppGuard],
        canDeactivate: [UnsavedChangesGuard],
      },
      // A bare `/spaces` names no space, and since chat is for TEAM workspaces only (Pavel,
      // 2026-09-24) there is nothing to choose between: the account names its one chat. So this
      // renders nothing -- `chatHomeGuard` always answers with a UrlTree, to the team's own chat or
      // to Mail. `pathMatch: 'full'` is load-bearing: without it this prefix would swallow
      // `/spaces/:spaceId/chat` below, which is the address the product hangs on.
      { path: SPACES_PATH, pathMatch: 'full', canActivate: [chatHomeGuard], children: [] },
      // One lazy entry for every chat address; the loaded tree owns the persistent layout and its guarded views.
      {
        path: `${SPACES_PATH}/:spaceId/chat`,
        loadChildren: () => import('@modules/spaces/chat/chat.routes').then((m) => m.CHAT_ROUTES),
      },
      // Bare chat names no space, so the account decides which one it means -- the same resolver
      // the bare `/spaces` above uses. A guard rather than a `redirectTo`, because the answer comes
      // from the account and a `redirectTo` function is synchronous.
      { path: 'chat', pathMatch: 'full', canActivate: [chatHomeGuard], children: [] },
      { path: 'chat/:channelId', pathMatch: 'full', canActivate: [chatHomeGuard], children: [] },
      {
        path: 'events',
        component: EventsLayoutComponent,
        canActivate: [CalendarAppGuard],
        canDeactivate: [UnsavedChangesGuard],
      },
      {
        path: 'tasks',
        component: TaskingLayoutComponent,
        canActivate: [TaskingAppGuard],
        canDeactivate: [UnsavedChangesGuard],
      },
      {
        path: `${SPACES_PATH}/:spaceId/notes`,
        component: NotesLayoutComponent,
        canActivate: [NotesAppGuard, notesWorkspaceGuard],
        canDeactivate: [notesWorkspaceCanDeactivate],
      },
      {
        path: 'notes',
        component: NotesLayoutComponent,
        canActivate: [NotesAppGuard, notesHomeGuard],
        canDeactivate: [notesWorkspaceCanDeactivate],
      },
      // Atlas, inside the app shell. `AtlasAppGuard` runs FIRST — it refuses before the space
      // membership / home resolver does any work, and it is what gates both atlasView and whether the
      // deployment runs Atlas at all. The page is the ONLY lazy reference to `@modules/atlas`: nothing
      // here imports it statically, so none of it rides the eager bundle. The space-scoped address
      // reuses Notes' workspace resolver; the bare one reuses Notes' home resolver, as the Notes
      // routes above do.
      {
        path: 'atlas',
        component: AtlasLayoutComponent,
        canActivate: [AtlasAppGuard],
        canDeactivate: [UnsavedChangesGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@modules/pages/components/atlas-proto/atlas-proto-page.component').then((m) => m.AtlasProtoPageComponent),
          },
        ],
      },
      {
        // Sheets inside the app shell, where production Sheets belongs (AM-27).
        path: 'sheets',
        component: SheetsLayoutComponent,
        canActivate: [SheetsAppGuard],
        canDeactivate: [UnsavedChangesGuard],
        children: [
          {
            // Lazy, and only on 'open': /sheets shows a cheap preview, so the
            // 1.87 MB Sheets chunk is fetched when a workbook is actually opened.
            path: 'open',
            canDeactivate: [sheetsEditorCanDeactivate],
            runGuardsAndResolvers: 'always',
            // B-189: one editor per workbook id (see SheetsOpenComponent).
            loadComponent: () =>
              import('@modules/sheets/components/sheets-open/sheets-open.component').then((m) => m.SheetsOpenComponent),
          },
        ],
      },
      {
        path: 'contacts',
        component: ContactsLayoutComponent,
        canActivate: [ContactsAppGuard],
        canDeactivate: [UnsavedChangesGuard],
      },
      {
        path: 'files',
        component: FilesLayoutComponent,
        canActivate: [FilesAppGuard],
        canDeactivate: [UnsavedChangesGuard],
      },
      {
        path: 'slides',
        component: LazySlidesViewComponent,
        canActivate: [SlidesAppGuard],
        canDeactivate: [UnsavedChangesGuard],
      },
      {
        // R04: the Boards shell (library, item and editor in one routed page, as Slides). It matches only for an account whose
        // Boards view permission is on; anyone else falls through to the sentinel below, whose `/boards` answers "not available".
        path: 'boards',
        pathMatch: 'full',
        component: BoardsLayoutComponent,
        canMatch: [boardsAppEnabledMatch],
        canDeactivate: [UnsavedChangesGuard, boardsCanDeactivate],
      },
      {
        // Sentinel: hidden from the app picker, answers with the unavailable state; deeper links are guarded inside.
        path: 'boards',
        loadChildren: () => import('@modules/boards/boards.routes').then((m) => m.BOARDS_ROUTES),
      },
    ],
  },
  {
    path: 'shared',
    component: SharedLayoutComponent,
    children: [
      { path: 'message-folder/:id', component: MessageFolderSharedComponent },
      { path: 'message/:id', component: MessageSharedComponent },
      { path: 'calendar/:id', component: CalendarSharedComponent },
      { path: 'event/:id', component: EventSharedComponent },
      { path: 'project/:id', component: ProjectSharedComponent },
      { path: 'task/:id', component: TaskSharedComponent },
      { path: 'notebook/:id', component: NotebookSharedComponent },
      { path: 'note/:id', component: NoteSharedComponent },
      { path: 'group/:id', component: GroupSharedComponent },
      { path: 'contact/:id', component: ContactSharedComponent },
      { path: 'folder/:id', component: FolderSharedComponent },
      { path: 'file/:id', component: FileSharedComponent },
      { path: 'sheet-folder/:id', component: SheetFolderSharedComponent },
      { path: 'sheet/:id', component: SheetSharedComponent },
      { path: 'slides/:id', component: LazySlidesViewComponent, data: { slidesView: 'shared' } },
      // R10: a board's read-only link. Lazy, so the public page and Excalidraw stay out of the eager bundle.
      { path: 'board/:id', loadComponent: () => import('@modules/boards/share/board-shared.component').then((m) => m.BoardSharedComponent) },
    ],
  },
  // Speaker View was removed; a bookmarked or still-open speaker link lands on Slides rather than on Mail.
  { path: 'slides/speaker/:id', redirectTo: '/slides', pathMatch: 'full' },
  { path: '**', redirectTo: '/mail', pathMatch: 'full' },
];

@NgModule({
  imports: [RouterModule.forChild(pagesRoutes)],
  exports: [RouterModule],
})
export class PagesRoutingModule {}
